import { FC, Fragment, lazy, ReactNode, useEffect, useState } from "react";
import { AppContext } from "Context/AppContext";
import { ConfigurationClient, ConfigurationGetResponse, CountryOption, ResourceClient, SessionClient, SessionGetResponse, StaticDataClient } from "ClientApi/ClientApi";
import { Layout } from "./Layout";
import { Routes, Route, useSearchParams } from "react-router";
import { Dictionary, HttpStatusCode, replaceStringVariables, withRetry } from "aphrodite.react/Helpers";
import { Language } from "./Helpers/Language";

const CustomerAreaLazy = lazy(() => import("./Pages/Customer/CustomerArea"));
const HomeAreaLazy = lazy(() => import("./Pages/Home/HomeArea"));

export const App: FC = () => {
    const [searchParams] = useSearchParams();

    const [resources, setResources] = useState<Dictionary<string> | null>(null);
    const [session, setSession] = useState<SessionGetResponse | null>(null);
    const [countries, setCountries] = useState<CountryOption[] | null>(null);
    const [configuration, setConfiguration] = useState<ConfigurationGetResponse | null>(null);
    // inApp disappears from the SearchParams after navigate, so store it in a state variable
    const [inApp] = useState<boolean>(searchParams.has("inApp"));

    const getResources = async () => {
        const languageId = await Language.get();

        const response = await withRetry(() => new ResourceClient().get(languageId));

        if (response.status === HttpStatusCode.Http200Ok) {
            setResources(response.result);
        }
    };

    const refreshSession = async () => {
        const response = await new SessionClient().get();

        switch (response.status) {
            case HttpStatusCode.Http200Ok:
                setSession(response.result);
                break;
            case HttpStatusCode.Http204NoContent:
                setSession(null);
                break;
        }

        return Promise.resolve();
    };

    const resource = (key: string | null, variables?: Dictionary<string | number>) => {
        if (key == null) return "";

        if (resources) {
            const result = resources[key];

            if (result) {
                return replaceStringVariables(result, variables);
            }
        }

        return `{{${key}}}`;
    };

    const resourceNode = (key: string | null, variables?: Dictionary<string | number | ReactNode>) => {
        if (key == null) return <></>;

        if (resources) {
            const result = resources[key];

            if (result) {
                const parts = result.split(/({[^}]+})/g);

                return (
                    <>
                        {parts.map((part, index) => {
                            const match = part.match(/{([^}]+)}/);
                            if (match) {
                                const varName = match[1];
                                const replacement = variables?.[varName];
                                return <Fragment key={index}>{replacement}</Fragment>;
                            }
                            return <Fragment key={index}>{part}</Fragment>;
                        })}
                    </>
                );
            }
        }

        // Resource not found
        return <>{`{{${key}}}`}</>;
    };

    const getCountries = async () => {
        const response = await withRetry(() => new StaticDataClient().getCountries());
        if (response.status === HttpStatusCode.Http200Ok) {
            setCountries(response.result);
        }
    };

    const getConfiguration = async () => {
        const response = await withRetry(() => new ConfigurationClient().get());
        if (response.status === HttpStatusCode.Http200Ok) {
            setConfiguration(response.result);
        }
    };

    useEffect(() => {
        getResources();
        refreshSession();
        getCountries();
        getConfiguration();
    }, []);

    if (!resources || !countries) return null;

    return (
        <AppContext.Provider value={{ resource, resourceNode, session, refreshSession, countries, configuration, inApp: inApp || searchParams.has("inApp"), noBackNav: searchParams.has("noBackNav") }}>
            <Layout>
                <Routes>
                    <Route index path="*" element={<HomeAreaLazy />} />
                    <Route path="app">
                        <Route index path="*" element={<CustomerAreaLazy />} />
                    </Route>
                </Routes>
            </Layout>
        </AppContext.Provider>
    );
};